import api from "./api";

export type GetAllSubscribersParams = {
  isActive: boolean;
  level1ResourceId: string;
  hasBalance: boolean;
  accountNumber: string;
};

export const getAllSubscribers = async (
  params?: Partial<GetAllSubscribersParams>,
): Promise<any> => {
  const { data } = await api.get("/admin/subscribers", { params });

  return data.subscribers;
};

export const getSubscriber = async (id: string): Promise<any> => {
  const { data } = await api.get(`/admin/subscribers/${id}`);
  return data.subscriber;
};

export const getSystemSubscriber = async (): Promise<any> => {
  const { data } = await api.get("/admin/subscribers/0");

  return data.subscriber;
};

export type Address = {
  id: string;
  street: string;
  town: string;
  region: string;
  postcode: string;
  country: string;
  location: string;
};

type SubscriberPayload = {
  level1ResourceId: string;
  name: string;
  abn?: string;
  acn?: string;
  accountNumber: string;
  accountType?: string;
  isActive: boolean;
  billingFrequency?: "Q" | "A" | "B";
  address?: Partial<Address>;
  startActiveDate: Date;
};

export const createSubscriber = async (payload: SubscriberPayload) => {
  const { data } = await api.post("/admin/subscribers", payload);

  return [data.subscriber, data.workflowInstance];
};

export const updateSubscriber = async ({
  subscriberId,
  ...payload
}: {
  subscriberId: string;
  primaryUserId?: string;
} & Partial<SubscriberPayload>) => {
  const { data } = await api.put(`/admin/subscribers/${subscriberId}`, payload);

  return [data.subscriber, data.workflowInstance];
};

export const generateSubscriberAccountNumber = async (): Promise<any> => {
  const { data } = await api.get("/admin/generateSubscriberAccountNumber");

  return data.accountNumber;
};

export const checkAccountNumber = async (subscriberData: {
  accountNumber: string;
  id?: string;
}): Promise<any> => {
  const { data } = await api.post(
    "/admin/subscribers/checkAccountNumber",
    subscriberData,
  );

  return data.exists;
};

export const linkLevel0Resource = async ({
  id,
  ...payload
}: {
  id: string;
  level0ResourceId: string;
}) => {
  const { data } = await api.post(
    `/admin/subscribers/${id}/linkLevel0WRS`,
    payload,
  );

  return [data.subscriber];
};

export const syncBillingGroups = async ({
  id,
  billingGroupIds,
}: {
  id: string;
  billingGroupIds: string[];
}) => {
  const { data } = await api.post(
    `/admin/subscribers/${id}/syncBillingGroups`,
    { billingGroupIds },
  );

  return [data.subscriber, data.workflowInstance];
};

export const getAllSubscriberLevel0Resources = async (subscriberId: string) => {
  const { data } = await api.get(
    `/admin/subscribers/${subscriberId}/level0Resources`,
  );

  return data.level0Resources;
};

export const getSubscriberLevel0Resource = async (params: {
  subscriberId?: string;
  level0ResourceId: string;
}) => {
  const { data } = params.subscriberId
    ? await api.get(
        `/admin/subscribers/${params.subscriberId}/level0Resources/${params.level0ResourceId}`,
      )
    : await api.get(`/subscribers/level0Resources/${params.level0ResourceId}`);

  return data.level0Resource;
};

export const getSubscriberLevel0Resources = async () => {
  const { data } = await api.get(`/subscribers/level0Resources`);

  return data.level0Resources;
};

export const getSubscriberSchemes = async () => {
  const { data } = await api.get("/subscribers/schemes");

  return data.schemes;
};

export const getSchemeSummary = async () => {
  const { data } = await api.get("/subscribers/schemes/summary");

  return data.scheme;
};

export const getSubscriberDeclarations = async (params?: {
  accountingPeriodId?: string;
}) => {
  const { data } = await api.get("/subscribers/declarations", { params });

  return data.declarations;
};

export const getSubscriberUsers = async () => {
  const { data } = await api.get("/subscribers/users");

  return data.users;
};
